<template>
  <div class="row">
    <div class="col-12" id="scrollContainer">
      <!-- Error Display -->
      <div v-if="errors.general" class="alert alert-danger" role="alert">
        <strong>Fout:</strong> {{ errors.general }}
        <button @click="errors.general = ''" class="close-btn">×</button>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
        <span>Configuratie laden...</span>
      </div>

      <StepHeading
        flowType="compose"
        :currentStep="currentStepIndex"
        :configData="configData"
        @go-to-step="goToStep"
      />
    </div>
    <div class="col-12 col-xl-9">
      <Steps
        ref="steps"
        flowType="compose"
        :currentStepId="currentStepId"
        :data="configData"
        :errors="errors"
        @update="onUpdateData"
        @next="nextStep"
        @go-to-step="goToStep"
        @reset-package="resetPackage"
        @printing-updated="onPrintingUpdated"
      />
    </div>
    <div class="col-12 col-xl-3">
      <Sidebar
        :data="configData"
        :currentStep="currentStepIndex"
        flowType="compose"
        :isCustomFlow="true"
        :packageQuantity="configData.quantity"
        :cartItemKey="cartItemKey"
        @increase-product="increaseProduct"
        @decrease-product="decreaseProduct"
        @add-to-cart="handleAddToCart"
        @go-to-step="goToStep"
        @reset-package="resetPackage"
        @remove-product="removeProduct"
        @remove-food="removeFoodDrink"
        @remove-packaging="removePackaging"
        @remove-card="removeCard"
        @update-products="updateProductsFromSidebar"
        @update-fooddrinks="updateFoodDrinksFromSidebar"
        @update:packageQuantity="updateQuantity"
        @clear-draft="clearDraft"
        @show-package-type-modal="handleShowPackageTypeModal"
      />
    </div>
  </div>

  <!-- Select package type modal -->
  <PackageTypeModal
    v-if="showPackageTypeModal === true"
    flowType="compose"
    :package-type-image-box="packageTypeImageBox || undefined"
    :package-type-image-mailbox="packageTypeImageMailbox || undefined"
    :preselected-product="preselectedProductInfo"
    @confirm="handlePackageTypeConfirm"
    @close-modal="handleCloseModal"
  />

  <!-- Add to Cart modal -->
  <AddToCartModal
    v-if="showAddToCartModal"
    :packageData="addToCartModalData"
    :packageType="packageType"
    :isSaving="isSaving"
    @close-modal="handleCloseAddToCartModal"
    @open-package-type-modal="handleOpenPackageTypeModal"
  />

  <!-- Continue banner -->
  <ContinueBanner
    :show="showContinueBanner && cartItemKey === null"
    :summary="draftSummary"
    @continue="handleContinueDraft"
    @new="handleNewConfiguration"
    @dismiss="handleDismissBanner"
  />
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import Sidebar from './Sidebar/Sidebar.vue';
import StepHeading from './StepHeading.vue';
import Steps from './Steps.vue';
import PackageTypeModal from './modals/PackageTypeModal.vue';
import AddToCartModal from './modals/AddToCartModal.vue';
import ContinueBanner from './ContinueBanner.vue';
import { getFlowSteps, getStepByIndex } from './config/flowConfig.js';
import {
  increaseProductCount,
  decreaseProductCount,
  removeProduct as removeProductUtil,
  removePersonalization as removePersonalizationUtil,
  cleanupUrlParameter,
  getUrlParameter,
} from './utils/productCountUtils.js';
import {
  calculateVatBreakdownByProduct,
  roundCurrency,
} from './utils/priceUtils.js';
import { useAutoSave } from '../../composables/useAutoSave.js';
import { useCartLinkInterceptor } from '../../composables/useCartLinkInterceptor.js';
import {
  determineModalContext,
  getContextOptions,
  initializePrefilledPackageTypeQuantity,
  MODAL_CONTEXT,
} from './utils/contextAwareModal.js';

export default {
  name: 'Compose',
  components: {
    PackageTypeModal,
    AddToCartModal,
    Sidebar,
    StepHeading,
    Steps,
    ContinueBanner,
  },
  setup() {
    const packageType = ref('large');
    const currentStepIndex = ref(0);
    const configData = ref({
      quantity: 10,
      packageType: 'large',
      products: [],
      foodDrinks: [],
      packaging: null,
      packagingOptions: [],
      packagingMode: 'box',
      packagingProduct: null,
      personalMessage: '',
      personalization: {
        productPersonalization: {},
        personalMessage: '',
      },
      deliveryInfo: {},
      isMailboxSession: false,
    });
    const errors = ref({});
    const isLoading = ref(false);
    const cartItemKey = ref(null);
    const showContinueBanner = ref(false);
    const showAddToCartModal = ref(false);
    const addToCartModalData = ref({});

    const modalContext = ref(null);
    const quantityFromUrlParam = ref(null); // Track if we got quantity from URL

    const flowType = ref('compose');

    const packageTypeImageBox = computed(
      () =>
        (typeof window !== 'undefined' &&
          window.__S7_PACKAGE_TYPE_IMAGES__?.box) ||
        '',
    );
    const packageTypeImageMailbox = computed(
      () =>
        (typeof window !== 'undefined' &&
          window.__S7_PACKAGE_TYPE_IMAGES__?.mailbox) ||
        '',
    );

    const {
      isSaving,
      lastSaved,
      saveError,
      hasUnsavedChanges,
      showPackageTypeModal,
      clearDraft,
      getDraftSummary,
      restoreDraft,
    } = useAutoSave(configData, flowType, currentStepIndex);

    const draftSummary = ref(null);

    const updateDraftSummary = async () => {
      const summary = await getDraftSummary();
      draftSummary.value = summary;
    };

    const flowSteps = computed(() => {
      return getFlowSteps('compose');
    });

    const totalSteps = computed(() => {
      return flowSteps.value.length - 1;
    });

    const currentStepId = computed(() => {
      const step = getStepByIndex('compose', currentStepIndex.value);
      return step ? step.id : 'producten';
    });

    const checkForDraft = async () => {
      await updateDraftSummary();
      if (draftSummary.value && draftSummary.value.flowType === 'compose') {
        showContinueBanner.value = true;
      }
    };

    const handleContinueDraft = () => {
      if (restoreDraft()) {
        if (configData.value.packageType === 'small') {
          configData.value.isMailboxSession = true;
          sessionStorage.setItem('s7_is_mailbox_session', 'true');
        }
        showContinueBanner.value = false;

        nextTick(() => {
          addPreselectedProduct();
        });
      }
    };

    const handleNewConfiguration = () => {
      clearDraft();
      showContinueBanner.value = false;
      addPreselectedProduct();
      loadSavedPackageData();
    };

    const handleDismissBanner = () => {
      showContinueBanner.value = false;
      nextTick(() => {
        if (preselectedProductInfo.value) {
          addPreselectedProduct();
        }
      });
    };

    const reEvaluateContext = () => {
      const contextOptions = getContextOptions(configData.value);
      const contextResult = determineModalContext(contextOptions);
      modalContext.value = contextResult.context;
      showPackageTypeModal.value = contextResult.showModal;
      return contextResult;
    };

    const loadSavedPackageData = () => {
      const contextOptions = getContextOptions(configData.value);
      const contextResult = determineModalContext(contextOptions);
      modalContext.value = contextResult.context;

      const hasProductInUrl =
        new URLSearchParams(window.location.search).get('product') ||
        (window.location.hash && window.location.hash.match(/product=(\d+)/));

      const hasPreselectedProduct = preselectedProductInfo.value !== null;

      if (contextResult.context === MODAL_CONTEXT.FROM_SCRATCH) {
        if (!hasProductInUrl && !hasPreselectedProduct) {
          configData.value.products = [];
          configData.value.foodDrinks = [];
        }
        configData.value.packaging = null;
        configData.value.basePackage = null;
        configData.value.isBasePackage = false;
        configData.value.packageType = 'large';
        packageType.value = 'large';
        sessionStorage.removeItem('s7_package_type');
        sessionStorage.removeItem('s7_package_quantity');
      }

      const savedType = sessionStorage.getItem('s7_package_type');
      const savedQuantity = sessionStorage.getItem('s7_package_quantity');

      if (savedType) {
        packageType.value = savedType;
        configData.value.packageType = savedType;
      }

      if (!quantityFromUrlParam.value) {
        const aantalParam = getUrlParameter('aantal');
        if (aantalParam) {
          const quantity = parseInt(aantalParam);
          if (quantity > 0) {
            quantityFromUrlParam.value = quantity;
            configData.value.quantity = quantity;
            sessionStorage.setItem('s7_package_quantity', String(quantity));
            cleanupUrlParameter('aantal');
          }
        }
      }

      if (quantityFromUrlParam.value) {
        configData.value.quantity = quantityFromUrlParam.value;
      } else if (savedQuantity) {
        configData.value.quantity = parseInt(savedQuantity);
      }

      if (
        contextResult.context === MODAL_CONTEXT.PREFILLED_BASE_PACKAGE &&
        contextResult.needsTypeQuantityInit
      ) {
        const { packageType: initType, quantity: initQuantity } =
          initializePrefilledPackageTypeQuantity(
            configData.value,
            configData.value.basePackage,
          );
        configData.value.packageType = initType;
        // Only use default quantity if no URL quantity was provided
        if (!quantityFromUrlParam.value) {
          configData.value.quantity = initQuantity;
        }
        packageType.value = initType;
      }

      showPackageTypeModal.value = contextResult.showModal;

      if (contextResult.context === MODAL_CONTEXT.RESUME_VIA_BANNER) {
        setTimeout(checkForDraft, 0);
      }
    };

    const isEditMode = () => {
      return cartItemKey.value !== null && cartItemKey.value >= 0;
    };

    const loadEditData = () => {
      try {
        isLoading.value = true;

        const sessionConfigStorage =
          sessionStorage.getItem('s7_current_config');
        const cartItemKeyStorage = sessionStorage.getItem('s7_cart_item_key');
        const isMailboxSession =
          sessionStorage.getItem('s7_is_mailbox_session') === 'true';

        if (sessionConfigStorage) {
          const parsedSessionData = JSON.parse(sessionConfigStorage);
          if (validateConfigData(parsedSessionData)) {
            configData.value = { ...configData.value, ...parsedSessionData };
            cartItemKey.value = cartItemKeyStorage
              ? parseInt(cartItemKeyStorage)
              : null;

            // Set mailbox session flag for filtering
            if (isMailboxSession) {
              configData.value.isMailboxSession = true;
              configData.value.packageType = 'small';
            }

            const targetStep = sessionStorage.getItem('s7_target_step');
            const hash = window.location.hash.replace('#', '');
            const stepMap = {
              products: 0,
              foodDrinks: 1,
              packaging: 2,
              personalization: 3,
            };

            if (targetStep && stepMap[targetStep] !== undefined) {
              currentStepIndex.value = stepMap[targetStep];
            } else if (hash && stepMap[hash] !== undefined) {
              currentStepIndex.value = stepMap[hash];
            }

            // Don't remove cart_item_key yet - we need it to detect edit mode
            sessionStorage.removeItem('s7_current_config');
            sessionStorage.removeItem('s7_target_step');

            // Trigger product reload to apply mailbox filtering
            setTimeout(() => {
              document.dispatchEvent(
                new CustomEvent('s7_reload_products', {
                  detail: { packageType: configData.value.packageType },
                }),
              );
            }, 100);

            return;
          }
        }

        const editData = localStorage.getItem('editConfigData');
        const editStepIndex = localStorage.getItem('editStepIndex');

        if (editData && editStepIndex) {
          const parsedData = JSON.parse(editData);
          if (validateConfigData(parsedData)) {
            configData.value = { ...configData.value, ...parsedData };
            currentStepIndex.value = validateStepIndex(parseInt(editStepIndex));
          } else {
            showError('Ongeldige configuratie data geladen. Start opnieuw.');
          }

          localStorage.removeItem('editConfigData');
          localStorage.removeItem('editStepIndex');
        }
      } catch (error) {
        console.error(
          'Er is een probleem opgetreden bij het laden van je configuratie.',
          error,
        );
        showError(
          'Er is een probleem opgetreden bij het laden van je configuratie.',
        );
      } finally {
        isLoading.value = false;
      }
    };

    const validateConfigData = (data) => {
      if (!data || typeof data !== 'object') {
        return false;
      }

      if (
        data.quantity &&
        (isNaN(data.quantity) || data.quantity < 10 || data.quantity > 99999)
      ) {
        return false;
      }

      if (data.products && !Array.isArray(data.products)) {
        return false;
      }

      if (data.foodDrinks && !Array.isArray(data.foodDrinks)) {
        return false;
      }

      return true;
    };

    const validateStepIndex = (stepIndex) => {
      const maxStep = totalSteps.value;
      if (isNaN(stepIndex) || stepIndex < 0 || stepIndex > maxStep) {
        return 0;
      }
      return stepIndex;
    };

    const showError = (message) => {
      errors.value.general = message;
      setTimeout(() => {
        errors.value.general = '';
      }, 5000);
    };

    const updateProductsFromSidebar = (newProducts) => {
      configData.value.products = [...newProducts];
    };

    const updateFoodDrinksFromSidebar = (newFoodDrinks) => {
      configData.value.foodDrinks = [...newFoodDrinks];
    };

    const onUpdateData = (newData) => {
      try {
        if (!newData || typeof newData !== 'object') return;

        showContinueBanner.value = false;

        if (newData.removeProductFromSelection) {
          const productId = newData.removeProductFromSelection;
          configData.value.products = configData.value.products.filter(
            (product) => product.id !== productId,
          );
          delete newData.removeProductFromSelection;
        }

        for (const key in newData) {
          if (configData.value.hasOwnProperty(key)) {
            if (
              Array.isArray(newData[key]) &&
              Array.isArray(configData.value[key])
            ) {
              configData.value[key] = newData[key].filter((item) =>
                validateArrayItem(item, key),
              );
            } else {
              configData.value[key] = validateDataValue(newData[key], key);
            }
          } else if (key === 'personalization') {
            configData.value.personalization = validateDataValue(
              newData[key],
              key,
            );
          }
        }

        errors.value = {};
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het bijwerken van je configuratie.',
        );
      }
    };

    const onPrintingUpdated = (printingData) => {
      try {
        if (!printingData || typeof printingData !== 'object') return;

        if (!configData.value.personalization)
          configData.value.personalization = {};

        configData.value.personalization.printing = {
          counts: printingData.counts,
          breakdown: printingData.breakdown,
          total_ex_vat: printingData.breakdown.totalExVat,
          // Preserve per-package data if it exists
          per_package: printingData.per_package || null,
        };

        errors.value = {};
      } catch (error) {
        // Silent fail for production
      }
    };

    const validateArrayItem = (item, arrayKey) => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      if (arrayKey === 'products' || arrayKey === 'foodDrinks') {
        if (!item.id || !item.name || typeof item.price !== 'number') {
          return false;
        }

        if (item.count && (isNaN(item.count) || item.count < 1)) {
          return false;
        }
      }

      return true;
    };

    const validateDataValue = (value, key) => {
      switch (key) {
        case 'quantity':
          const qty = parseInt(value);
          return !isNaN(qty) && qty >= 1 && qty <= 99999 ? qty : 1;
        case 'personalMessage':
          return typeof value === 'string' ? value : '';
        case 'packaging':
          return value && typeof value === 'object' ? value : null;
        case 'packagingOptions':
          return Array.isArray(value) ? value : [];
        case 'packagingMode':
          return value === 'product' || value === 'box' ? value : 'box';
        case 'packagingProduct':
          return value && typeof value === 'object' ? value : null;
        case 'basePackage':
          return value && typeof value === 'object' ? value : null;
        case 'personalization':
          if (value && typeof value === 'object') {
            const existingPersonalization =
              configData.value.personalization || {};
            return {
              ...existingPersonalization,
              ...value,
              printing: value.printing || existingPersonalization.printing,
            };
          }
          return {
            productPersonalization: {},
            personalMessage: '',
          };
        case 'deliveryInfo':
          return value && typeof value === 'object' ? value : {};
        default:
          return value;
      }
    };

    const resetPackage = (confirmDialog = true) => {
      try {
        if (
          confirmDialog &&
          !confirm('Weet je zeker dat je alles wilt resetten?')
        ) {
          return;
        }

        configData.value = {
          quantity: 10,
          packageType: 'large',
          products: [],
          foodDrinks: [],
          packaging: null,
          packagingOptions: [],
          packagingMode: 'box',
          packagingProduct: null,
          personalMessage: '',
          personalization: { productPersonalization: {}, personalMessage: '' },
          deliveryInfo: {},
        };
        packageType.value = 'large';
        currentStepIndex.value = 0;
        errors.value = {};
        sessionStorage.removeItem('s7_package_type');
        sessionStorage.removeItem('s7_package_quantity');
        sessionStorage.removeItem('s7_is_mailbox_session');

        if (confirmDialog) {
          loadSavedPackageData();
        }
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het resetten van je pakket.',
        );
      }
    };

    const nextStep = () => {
      try {
        if (currentStepIndex.value < totalSteps.value) {
          if (validateCurrentStep()) {
            currentStepIndex.value++;
            hasUnsavedChanges.value = false;
            setTimeout(() => {
              const container = document.getElementById('scrollContainer');
              if (container) container.scrollIntoView({ behavior: 'smooth' });
            }, 0);
          } else {
            showError(
              'Vul eerst alle verplichte velden in voordat je verder gaat.',
            );
          }
        }
      } catch (error) {
        showError('Er is een probleem opgetreden bij het navigeren.');
      }
    };

    const previousStep = () => {
      try {
        if (currentStepIndex.value > 0) {
          currentStepIndex.value--;
          hasUnsavedChanges.value = false;
          setTimeout(() => {
            const container = document.getElementById('scrollContainer');
            if (container) container.scrollIntoView({ behavior: 'smooth' });
          }, 0);
        }
      } catch (error) {
        showError('Er is een probleem opgetreden bij het navigeren.');
      }
    };

    const goToStep = (stepIndex) => {
      try {
        if (
          typeof stepIndex === 'number' &&
          stepIndex >= 0 &&
          stepIndex <= totalSteps.value
        ) {
          currentStepIndex.value = stepIndex;
          setTimeout(() => {
            const container = document.getElementById('scrollContainer');
            if (container) container.scrollIntoView({ behavior: 'smooth' });
          }, 0);
        } else {
          if (stepIndex < 0) currentStepIndex.value = 0;
          else if (stepIndex > totalSteps.value)
            currentStepIndex.value = totalSteps.value;
        }
      } catch (error) {
        showError('Er is een probleem opgetreden bij het navigeren.');
      }
    };

    const validateCurrentStep = () => {
      const currentStep = flowSteps.value[currentStepIndex.value];
      if (!currentStep) return true;

      switch (currentStep.id) {
        case 'producten':
          return (
            configData.value.products.length > 0 || configData.value.basePackage
          );
        case 'eten-drinken':
          return true;
        case 'verpakking':
          return configData.value.packaging !== null;
        case 'personalisatie':
          return true;
        default:
          return true;
      }
    };

    const increaseProduct = (productId) => {
      try {
        if (!productId) return;
        configData.value.products = increaseProductCount(
          configData.value.products,
          { id: productId },
        );
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het toevoegen van het product.',
        );
      }
    };

    const decreaseProduct = (productId) => {
      try {
        if (!productId) return;
        configData.value.products = decreaseProductCount(
          configData.value.products,
          productId,
        );
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verminderen van het product.',
        );
      }
    };

    const removeProduct = (productId) => {
      try {
        if (!productId) return;
        configData.value.products = removeProductUtil(
          configData.value.products,
          productId,
        );
        configData.value.personalization.productPersonalization =
          removePersonalizationUtil(
            configData.value.personalization.productPersonalization,
            productId,
          );
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verwijderen van het product.',
        );
      }
    };

    const removeFoodDrink = (foodId) => {
      try {
        if (!foodId) return;
        configData.value.foodDrinks = configData.value.foodDrinks.filter(
          (f) => f.id !== foodId,
        );
        configData.value.personalization.productPersonalization =
          removePersonalizationUtil(
            configData.value.personalization.productPersonalization,
            foodId,
          );
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verwijderen van het eten/drinken.',
        );
      }
    };

    const removePackaging = () => {
      try {
        configData.value.packaging = null;
        configData.value.packagingMode = 'box';
        configData.value.packagingProduct = null;
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verwijderen van de verpakking.',
        );
      }
    };

    const removeCard = () => {
      try {
        configData.value.personalization.selectedCard = null;
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verwijderen van de kaart.',
        );
      }
    };

    const handleAddToCart = () => {
      try {
        const packageData = {
          packageName:
            configData.value.packageName || 'Eigen samengesteld pakket',
          basePackage: configData.value.basePackage || null,
          isBasePackage: false,
          packageType: configData.value.packageType || 'large',
          quantity: configData.value.quantity,
          s7_is_mailbox_package: configData.value.packageType === 'small',
          products: configData.value.products
            .filter((product) => (product.count || 0) > 0)
            .map((product) => ({
              ...product,
              quantity: product.count || product.quantity || 1,
              count: product.count || product.quantity || 1,
              taxClass: product.taxClass,
              taxRate: product.taxRate,
              taxLabel: product.taxLabel,
            })),
          foodDrinks: configData.value.foodDrinks
            .filter((food) => (food.count || 0) > 0)
            .map((food) => ({
              ...food,
              quantity: food.count || food.quantity || 1,
              count: food.count || food.quantity || 1,
              taxClass: food.taxClass,
              taxRate: food.taxRate,
              taxLabel: food.taxLabel,
            })),
          packaging: (() => {
            if (
              configData.value.packagingProduct &&
              !configData.value.packaging
            ) {
              return configData.value.packagingProduct;
            }

            return configData.value.packaging;
          })(),
          packagingOptions: configData.value.packagingOptions,
          packagingMode: configData.value.packagingMode || 'box',
          packagingProduct: configData.value.packagingProduct || null,
          personalization: {
            printworkCount: configData.value.products.filter(
              (p) => p.printwork && (p.count || 0) > 0,
            ).length,
            customDesign: !!configData.value.personalMessage,
            customDesignPrice: configData.value.personalMessage ? 2.98 : 0,
            productPersonalization:
              configData.value.personalization?.productPersonalization || {},
            selectedCard:
              configData.value.personalization?.selectedCard || null,
            printNotes: configData.value.personalization?.printNotes || '',
            printing: {
              ...configData.value.personalization?.printing,
              per_package:
                configData.value.personalization?.printing?.per_package,
            },
            logoAttachments:
              configData.value.personalization?.logoAttachments || [],
          },
          deliveryInfo: configData.value.deliveryInfo,
          timestamp: Date.now(),
        };

        const stored = localStorage.getItem('composedPackages');
        const existingPackages = stored ? JSON.parse(stored) : [];

        if (
          cartItemKey.value !== null &&
          cartItemKey.value >= 0 &&
          cartItemKey.value < existingPackages.length
        ) {
          existingPackages[cartItemKey.value] = packageData;
        } else {
          existingPackages.push(packageData);
        }

        localStorage.setItem(
          'composedPackages',
          JSON.stringify(existingPackages),
        );
        sessionStorage.removeItem('s7_package_type');
        sessionStorage.removeItem('s7_package_quantity');

        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });

          const items = [];
          const basePackageId = packageData.basePackage?.id || 0;
          const basePackageName =
            packageData.basePackage?.name ||
            packageData.packageName ||
            'Onbekend pakket';

          const packageQuantity = packageData.quantity || 1;

          const filteredProducts = (packageData.products || []).filter(
            (product) => {
              if (
                (packageData.packaging &&
                  packageData.packaging.id === product.id) ||
                (packageData.packagingProduct &&
                  packageData.packagingProduct.id === product.id)
              ) {
                return false;
              }
              return true;
            },
          );

          const allProducts = [
            ...filteredProducts,
            ...(packageData.foodDrinks || []),
            ...(packageData.packaging ? [packageData.packaging] : []),
          ].filter(Boolean);

          allProducts.forEach((product) => {
            if (!product.id || !product.name) {
              return;
            }

            const productQuantityPer = product.quantity || product.count || 1;
            const productQuantity = productQuantityPer * packageQuantity;
            const productPrice = parseFloat(product.price || 0);

            items.push({
              item_id: product.id,
              item_name: product.name,
              quantity: productQuantity,
              price: productPrice,
              item_list_id: basePackageId,
              item_list_name: basePackageName,
              quantity_item_list: packageQuantity,
              quantity_per: productQuantityPer,
            });
          });

          let totalValue = items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          );

          let printingCostPerPackage = 0;
          if (
            packageData.personalization?.printing?.per_package?.total_ex_vat
          ) {
            printingCostPerPackage =
              packageData.personalization.printing.per_package.total_ex_vat;
          } else if (packageData.personalization?.printing?.total_ex_vat) {
            printingCostPerPackage =
              packageData.personalization.printing.total_ex_vat /
              packageQuantity;
          }

          const totalPrintingCost = printingCostPerPackage * packageQuantity;

          if (totalPrintingCost > 0) {
            items.push({
              item_id: 'printing',
              item_name: 'Bedrukking',
              quantity: packageQuantity,
              price: printingCostPerPackage,
              item_list_id: basePackageId,
              item_list_name: basePackageName,
              quantity_item_list: packageQuantity,
              quantity_per: 1,
            });
          }

          totalValue += totalPrintingCost;

          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'EUR',
              value: totalValue,
              items: items,
            },
          });
        }

        // Defer heavy operations to next tick
        requestAnimationFrame(() => {
          try {
            clearDraft();

            if (isEditMode()) {
              // Clear cart item key now that we've saved
              sessionStorage.removeItem('s7_cart_item_key');
              setTimeout(() => {
                window.location.href = '/winkelmandje';
              }, 200);
            } else {
              showAddToCartModal.value = true;
              addToCartModalData.value = packageData;
              resetPackage(false);
            }
          } catch (error) {
            console.error(
              'Error in handleAddToCart deferred operations:',
              error,
            );
          }
        });
      } catch (error) {
        console.error('Error in handleAddToCart:', error);
        // Show error to user
        showError(
          'Er is een fout opgetreden bij het opslaan van je pakket. Probeer het opnieuw.',
        );
      }
    };

    const updateQuantity = (newQuantity) => {
      try {
        const qty = parseInt(newQuantity);
        if (isNaN(qty) || qty < 1 || qty > 99999) {
          showError(
            'Ongeldige hoeveelheid. Gebruik een getal tussen 1 en 99999.',
          );
          return;
        }
        configData.value.quantity = qty;
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het bijwerken van de hoeveelheid.',
        );
      }
    };

    const handleCloseAddToCartModal = () => {
      clearDraft();
      resetPackage(false);
      showAddToCartModal.value = false;
      addToCartModalData.value = {};
    };

    const handleOpenPackageTypeModal = async () => {
      try {
        // Close the current modal first
        showAddToCartModal.value = false;

        // Small delay to ensure modal closes before opening new one
        await new Promise((resolve) => setTimeout(resolve, 100));

        clearDraft();
        sessionStorage.removeItem('s7_current_config');
        sessionStorage.removeItem('s7_cart_item_key');
        sessionStorage.removeItem('s7_target_step');
        resetPackage(false);

        // Open package type modal for new package
        showPackageTypeModal.value = true;
      } catch (error) {
        console.error('Error opening package type modal:', error);
        // Fallback: just close the current modal
        showAddToCartModal.value = false;
      }
    };

    const handleCloseModal = () => {
      showPackageTypeModal.value = false;
    };

    const handleShowPackageTypeModal = () => {
      showPackageTypeModal.value = true;
    };

    const handlePackageTypeConfirm = (packageType, quantity) => {
      configData.value.packageType = packageType;
      configData.value.quantity = quantity;

      sessionStorage.setItem('s7_package_type', packageType);
      sessionStorage.setItem('s7_package_quantity', String(quantity));

      if (packageType === 'small') {
        configData.value.isMailboxSession = true;
        sessionStorage.setItem('s7_is_mailbox_session', 'true');

        if (
          preselectedProductInfo.value &&
          !preselectedProductInfo.value.isBrievenbusCompatible
        ) {
          const { product, targetList } = preselectedProductInfo.value;
          configData.value[targetList] = (
            configData.value[targetList] || []
          ).filter((p) => p.id !== product.id);
          preselectedProductInfo.value = null;
        }
      } else {
        configData.value.isMailboxSession = false;
        sessionStorage.removeItem('s7_is_mailbox_session');
      }

      showPackageTypeModal.value = false;

      requestAnimationFrame(() => {
        document.dispatchEvent(
          new CustomEvent('s7_config_updated', { detail: { quantity } }),
        );
        document.dispatchEvent(
          new CustomEvent('s7_reload_products', { detail: { packageType } }),
        );
      });
    };

    const reloadStepDataWithFilter = () => {
      // Use requestAnimationFrame for better performance than setTimeout
      requestAnimationFrame(() => {
        try {
          document.dispatchEvent(
            new CustomEvent('s7_reload_products', {
              detail: { packageType: configData.value.packageType },
            }),
          );
        } catch (error) {
          console.error('Error in reloadStepDataWithFilter:', error);
        }
      });
    };

    const preselectedProductInfo = ref(null);

    const checkForPreselectedProduct = async () => {
      let productId = new URLSearchParams(window.location.search).get(
        'product',
      );

      if (!productId && window.location.hash) {
        const hashMatch = window.location.hash.match(/product=(\d+)/);
        if (hashMatch) {
          productId = hashMatch[1];
        }
      }

      if (!productId) {
        return;
      }

      try {
        const [productsRes, foodRes] = await Promise.allSettled([
          fetch(`/api/products/products`).then((r) => r.json()),
          fetch(`/api/products/food`).then((r) => r.json()),
        ]);

        let product = null;
        let targetList = null;

        if (productsRes.status === 'fulfilled' && productsRes.value?.items) {
          product = productsRes.value.items.find(
            (p) => String(p.id) === String(productId),
          );
          if (product) {
            targetList = 'products';
          }
        }

        if (
          !product &&
          foodRes.status === 'fulfilled' &&
          foodRes.value?.items
        ) {
          product = foodRes.value.items.find(
            (p) => String(p.id) === String(productId),
          );
          if (product) {
            targetList = 'foodDrinks';
          }
        }

        if (product && targetList) {
          const isBrievenbusCompatible = checkBrievenbusCompatibility(product);

          preselectedProductInfo.value = {
            product,
            targetList,
            isBrievenbusCompatible,
          };
        }
      } catch (error) {
        console.error('Error fetching preselected product:', error);
      }

      if (window.location.hash.includes('product=')) {
        history.replaceState(null, '', window.location.pathname);
      }
      const url = new URL(window.location);
      url.searchParams.delete('product');
      if (url.searchParams.toString()) {
        history.replaceState({}, '', url.toString());
      } else {
        history.replaceState({}, '', url.pathname);
      }
    };

    const addPreselectedProduct = () => {
      if (!preselectedProductInfo.value) {
        return;
      }

      const { product, targetList } = preselectedProductInfo.value;

      const existingProduct = configData.value[targetList]?.find(
        (p) => p.id === product.id,
      );
      if (!existingProduct) {
        configData.value[targetList] = [
          ...(configData.value[targetList] || []),
          { ...product, count: 1, quantity: 1, selected: true },
        ];
      }
    };

    const checkBrievenbusCompatibility = (product) => {
      if (!product || !product.attributes) return false;

      const attrs = product.attributes;
      const brievenbusAttr = attrs.find((attr) => {
        return (
          attr.name === 'pa_brievenbus-geschikt' ||
          attr.slug === 'pa_brievenbus-geschikt' ||
          attr.name?.toLowerCase().includes('brievenbus') ||
          attr.slug?.toLowerCase().includes('brievenbus')
        );
      });

      if (!brievenbusAttr) return false;

      const value = (
        brievenbusAttr.option ||
        brievenbusAttr.value ||
        ''
      ).toLowerCase();
      return value === 'ja' || value === 'yes' || value === '1';
    };

    // Intercept cart navigation when in edit mode
    useCartLinkInterceptor(isEditMode, handleAddToCart);

    onMounted(async () => {
      loadSavedPackageData();
      loadEditData();
      reEvaluateContext();
      await checkForPreselectedProduct();

      if (!isEditMode() && preselectedProductInfo.value) {
        await nextTick();
        setTimeout(() => {
          if (!showContinueBanner.value) {
            addPreselectedProduct();
          }
        }, 200);
      }
    });

    // Cleanup on unmount
    onUnmounted(() => {
      // Clear any pending timeouts
      if (timeoutId.value) {
        clearTimeout(timeoutId.value);
      }
    });

    return {
      packageTypeImageBox,
      packageTypeImageMailbox,
      showPackageTypeModal,
      packageType,
      currentStepIndex,
      configData,
      errors,
      isLoading,
      cartItemKey,
      showContinueBanner,
      showAddToCartModal,
      addToCartModalData,
      modalContext,
      draftSummary,
      isSaving,
      lastSaved,
      saveError,
      hasUnsavedChanges,
      flowSteps,
      totalSteps,
      currentStepId,
      checkForDraft,
      handleContinueDraft,
      handleNewConfiguration,
      handleDismissBanner,
      loadSavedPackageData,
      loadEditData,
      reEvaluateContext,
      validateConfigData,
      validateStepIndex,
      showError,
      updateProductsFromSidebar,
      updateFoodDrinksFromSidebar,
      onUpdateData,
      onPrintingUpdated,
      validateArrayItem,
      validateDataValue,
      resetPackage,
      nextStep,
      previousStep,
      goToStep,
      validateCurrentStep,
      increaseProduct,
      decreaseProduct,
      removeProduct,
      removeFoodDrink,
      removePackaging,
      removeCard,
      handleAddToCart,
      updateQuantity,
      handlePackageTypeConfirm,
      reloadStepDataWithFilter,
      handleCloseModal,
      handleCloseAddToCartModal,
      handleOpenPackageTypeModal,
      preselectedProductInfo,
    };
  },
};
</script>
